import React, { useEffect, useState } from 'react';
import { useTranslation } from '../hooks/useTranslation.js';
import { requestJson, fetchCurrentUser } from '../utils/authClient.js';
import { localizePath } from '../utils/localizedRoutes.js';
import { AdminPageHead, AdminSection } from '../components/AdminNav.jsx';
import { ActivityLog, AdminLink, FactGrid, McpServerTable, OrchestratorRuntime, ToolTable, UsageLedgerPanel, displayModelName, formatBerlin } from './VeridionShared.jsx';

const ACTIVITY_LIMIT = 120;

const errorMessageMap = {
  VERIDION_UNAVAILABLE: 'veridionUnavailable',
  VERIDION_NOT_CONFIGURED: 'veridionNotConfigured',
  VERIDION_REQUEST_FAILED: 'veridionRequestFailed',
};

// Re-exported for the company tab, which imports it from here.
export { McpServerTable };

// Veridion SMTM, the conversation core of the trading assistant. In the dashboard one section is on the
// screen at a time (IDD D-HP-ADMIN-040): `overview` (state and runtime), `tools`, `mcp`, `activity`, and `usage`,
// which the dashboard shows on its usage page beside the other ledgers. Alone, the page shows every section.
const VeridionPage = ({ embedded = false, section = '' }) => {
  const { t, currentLang } = useTranslation();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [health, setHealth] = useState(null);
  const [tools, setTools] = useState([]);
  const [servers, setServers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [usage, setUsage] = useState(null);

  const formatTimestamp = formatBerlin;
  const formatInteger = (value) => (value === null || value === undefined || value === '' ? '-' : Number(value).toLocaleString());
  const mapError = (value) => (value ? (t.admin?.[errorMessageMap[value]] || value) : '');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const currentUser = await fetchCurrentUser();
        setMe(currentUser.user);
        if (!currentUser.user?.roles?.includes('admin')) {
          setLoading(false);
          return;
        }

        const [healthResult, toolResult, serverResult, activityResult, usageResult] = await Promise.allSettled([
          requestJson('/api/admin/veridion-smtm/health'),
          requestJson('/api/admin/veridion-smtm/tools'),
          requestJson('/api/admin/veridion-smtm/mcp-servers'),
          requestJson(`/api/admin/veridion-smtm/activity?limit=${ACTIVITY_LIMIT}`),
          requestJson('/api/admin/veridion-smtm/usage?days=30'),
        ]);
        setUsage(usageResult.status === 'fulfilled' ? usageResult.value : null);

        if (healthResult.status === 'fulfilled') {
          setHealth(healthResult.value);
          setError('');
        } else {
          setHealth(null);
          setError(healthResult.reason?.code || 'VERIDION_UNAVAILABLE');
        }

        setTools(toolResult.status === 'fulfilled' ? (toolResult.value.items || []) : []);
        setServers(serverResult.status === 'fulfilled' ? (serverResult.value.items || []) : []);
        setActivity(activityResult.status === 'fulfilled' ? (activityResult.value.items || []) : []);
      } catch (requestError) {
        setError(requestError.code || 'VERIDION_UNAVAILABLE');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    if (!me?.roles?.includes('admin')) {
      return undefined;
    }

    let cancelled = false;
    const refresh = async () => {
      try {
        const payload = await requestJson(`/api/admin/veridion-smtm/activity?limit=${ACTIVITY_LIMIT}`);
        if (!cancelled) {
          setActivity(payload.items || []);
        }
      } catch {
        // Keep the last visible activity snapshot if a refresh fails.
      }
    };

    const intervalId = window.setInterval(refresh, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [me]);

  if (loading) {
    return <div className="site-card p-6">{t.auth.validating}</div>;
  }

  if (!me || !me.roles.includes('admin')) {
    return <main className="site-main"><div className="site-card p-6">{t.auth.errors.adminRequired}</div></main>;
  }

  const llm = health?.llm;
  const useLocalModel = llm?.useLocalModel ?? llm?.useLocalGptOss ?? false;
  const providerLabel = llm?.providerKind || (useLocalModel ? 'local' : '-');
  const modelLabel = displayModelName(llm?.loaded?.model || llm?.loadedModel || llm?.defaultModel) || '-';
  const providerAndModel = modelLabel === '-' ? providerLabel : `${providerLabel} / ${modelLabel}`;
  const connected = servers.filter((server) => server.status === 'connected').length;
  const n = t.admin.nav || {};

  // Embedded in the dashboard one section is shown at a time; the stand-alone page shows all.
  const show = (name) => !embedded || !section || section === name;

  const pageBody = (
    <>
      {!embedded ? (
        <AdminPageHead title={t.admin.veridionTitle} subtitle={t.admin.veridionSubtitle}
          actions={<a className="homepage-button-secondary" href={localizePath('/admin', currentLang)}>{t.nav?.admin}</a>} />
      ) : null}

      {show('overview') ? (
        <AdminSection id="smtm-veridion-state" title={t.admin.veridionCoreStatus} state={health?.status || '-'} tone={error ? 'error' : health?.status === 'ok' ? 'ok' : 'warning'}
          actions={(
            <>
              <a className="homepage-button-secondary" href="/api/admin/veridion-smtm/docs" target="_blank" rel="noreferrer">{t.admin.veridionOpenDocs}</a>
              <AdminLink tab="usage" section="smtm">{t.admin.ops?.usageTitle}</AdminLink>
            </>
          )}>
          {error ? <div className="homepage-muted-panel p-4 text-sm text-error">{mapError(error)}</div> : null}
          <FactGrid items={[
            [t.admin.veridionCoreStatus, health?.status || '-'],
            [t.admin.veridionProvider, providerAndModel],
            [t.admin.veridionTools, formatInteger(health?.toolCount || tools.length)],
            [t.admin.veridionMcpTitle, (n.connectedOf || '').replace('{connected}', connected).replace('{total}', servers.length)],
            [t.admin.veridionRuntimeProfiles, (health?.runtimeProfiles || []).join(', ') || '-'],
            [t.admin.veridionRegistryReloaded, formatTimestamp(health?.lastRegistryReload)],
          ]} />
        </AdminSection>
      ) : null}

      {show('overview') ? (
        <AdminSection id="smtm-veridion-runtime" title={t.admin.ops?.runtimeTitle} subtitle={t.admin.ops?.runtimeSubtitle} collapsed>
          <OrchestratorRuntime health={health} t={t} />
        </AdminSection>
      ) : null}

      {show('tools') ? (
        <AdminSection id="smtm-veridion-tools" title={t.admin.veridionToolsTitle} subtitle={t.admin.veridionToolsSubtitle} count={tools.length}>
          <ToolTable tools={tools} t={t} />
        </AdminSection>
      ) : null}

      {show('mcp') ? (
        <AdminSection id="smtm-veridion-mcp" title={t.admin.veridionMcpTitle} subtitle={t.admin.veridionMcpSubtitle}
          state={(n.connectedOf || '').replace('{connected}', connected).replace('{total}', servers.length)}
          tone={servers.some((server) => server.status === 'unreachable') ? 'error' : servers.length ? 'ok' : ''}>
          <McpServerTable servers={servers} t={t} formatTimestamp={formatTimestamp} />
        </AdminSection>
      ) : null}

      {show('usage') ? (
        <AdminSection id="smtm-veridion-usage" title={`${t.admin.tabVeridion}: ${t.admin.ops?.usageTitle}`} subtitle={t.admin.ops?.smtmUsageSubtitle}>
          <UsageLedgerPanel usage={usage} t={t} />
        </AdminSection>
      ) : null}

      {show('activity') ? (
        <AdminSection id="smtm-veridion-activity" title={t.admin.veridionActivityTitle} subtitle={t.admin.veridionActivitySubtitle} count={activity.length}>
          <ActivityLog activity={activity} t={t} formatTimestamp={formatTimestamp} withSource />
        </AdminSection>
      ) : null}
    </>
  );

  if (embedded) {
    return <div className="admin-page-body">{pageBody}</div>;
  }

  return <main className="site-main admin-dashboard-main admin-page-body">{pageBody}</main>;
};

export default VeridionPage;
